/**
 * Bulk first-interview invite — read, edit, then confirm once.
 *
 * The batch shares ONE template per stage, so this modal has two jobs:
 *
 *  1. Show the template that will actually go out, and let the recruiter FIX it
 *     here. The copy comes from the stage's Email Library entry (or the link's
 *     own, when someone authored one) and is right most of the time, but "most
 *     of the time" is not something to discover after eight emails have left.
 *  2. Show what every `{token}` resolves to PER CANDIDATE, because that is where
 *     a mis-configured interview link actually shows up: seven booking links and
 *     one blank cell.
 *
 * Edits re-render the table as they are typed (inviteRender.ts) — the values are
 * already known, so nothing needs re-fetching. An edit applies to THIS batch;
 * the note under the editor points at the two places that change it for good.
 *
 * The batch arrives PRE-TICKED — it is the selection the recruiter just made on
 * the tab, and making them make it twice was the single most-reported cost of
 * this screen (Theresa, Sep 2026). Only candidates who cannot be sent at all
 * are held back, and the modal says so in a line rather than leaving an empty
 * checkbox to be read as a bug. A flagged-but-sendable invitation is ticked and
 * LOUD — red row, red banner, counted in the confirm button — because the guard
 * that matters is the one the recruiter reads, not the one that silently costs
 * them twelve clicks. Nothing is sent from here: confirming hands the batch to
 * the board's countdown (usePendingActions), which does the sending.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import {
  fetchFirstInvitePreview,
  type FirstInvitePreview,
  type FirstInvitePreviewCandidate,
  type FirstInviteTemplate,
} from '../../services/firstInviteApi';
import type { FirstInterviewCandidate, RoleBoard } from '../../types/taActionBoard';
import { ReviewTable } from './BulkInviteTable';
import { COLORS } from './format';
import type { BryqKey } from '../../services/bryqApi';
import { BryqAlongsideField } from './BryqAlongsideField';
import { useBryqNote, withBryqNote } from './bryqNote';
import { TemplateEditor } from './BulkInviteTemplateEditor';
import { renderCandidate, type RenderedRow } from './inviteRender';
import { ModalActions } from './ModalActions';

export interface BulkInviteState {
  role: RoleBoard;
  candidates: FirstInterviewCandidate[];
}

/** Template text as edited in this modal, keyed by stage. */
type Edits = Record<string, { subject: string; body: string }>;

export function BulkInviteModal({
  state,
  boardEmail,
  senderName,
  onClose,
  onConfirm,
}: {
  state: BulkInviteState | null;
  boardEmail: string;
  senderName: string;
  onClose: () => void;
  /** Hand the vetted batch — with the edited text — to the board's countdown. */
  onConfirm: (role: RoleBoard, batch: FirstInvitePreviewCandidate[], language: 'de' | 'en') => void;
}) {
  if (!state) return null;
  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,64rem)] gap-6 overflow-auto rounded p-6 md:p-10">
        <BulkInviteBody
          state={state}
          boardEmail={boardEmail}
          senderName={senderName}
          onClose={onClose}
          onConfirm={onConfirm}
        />
      </ModalContent>
    </Modal>
  );
}

/** NOT named "Modal…" — see the ActionModals note on DS child cloning. */
function BulkInviteBody({
  state,
  boardEmail,
  senderName,
  onClose,
  onConfirm,
}: {
  state: BulkInviteState;
  boardEmail: string;
  senderName: string;
  onClose: () => void;
  onConfirm: (role: RoleBoard, batch: FirstInvitePreviewCandidate[], language: 'de' | 'en') => void;
}) {
  const { role, candidates } = state;
  const [language, setLanguage] = useState<'de' | 'en'>('de');
  const [bryq, setBryq] = useState<BryqKey | null>(null);
  const bryqNote = useBryqNote(language);
  const [preview, setPreview] = useState<FirstInvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [included, setIncluded] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);
  const [edits, setEdits] = useState<Edits>({});
  /**
   * `{ [stageId]: linkId }` — the recruiter's overrides where a stage has several live links. Empty
   * in the common case: the proxy derives the default (their own link, else the newest), and the
   * picker only appears when there is a choice to make. Per send, never saved.
   */
  const [linkChoices, setLinkChoices] = useState<Record<string, string>>({});
  /** A link change keeps the recruiter's ticks; a language change starts from the defaults. */
  const keepSelection = useRef(false);

  const opportunityIds = useMemo(() => candidates.map((c) => c.opportunityId), [candidates]);

  /* eslint-disable react-hooks/set-state-in-effect -- the preview IS this modal's
     content; there is nothing to render until it lands. */
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    fetchFirstInvitePreview({
      postingId: role.postingId,
      opportunityIds,
      roleTitle: role.roleTitle,
      boardEmail,
      senderName,
      language,
      linkChoices,
    })
      .then((data) => {
        if (!live) return;
        setPreview(data);
        // A language switch re-fetches, so edits in the old language are dropped
        // rather than silently pasted into the other language's template.
        setEdits({});
        const sendable = new Set(data.candidates.filter(canBeSent).map((c) => c.opportunityId));
        // Read BEFORE resetting: the updater below runs later, during render.
        const keep = keepSelection.current;
        keepSelection.current = false;
        setIncluded((prev) => (keep ? new Set([...prev].filter((id) => sendable.has(id))) : sendable));
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!live) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [role.postingId, role.roleTitle, opportunityIds, boardEmail, senderName, language, linkChoices]);

  /** Re-draft one stage on another link. Every booking URL in that stage changes with it. */
  const pickLink = (stageId: string, linkId: string) => {
    const edited = Object.keys(edits).length > 0;
    if (edited && !window.confirm('This will re-draft the invitations and discard your edits. Continue?')) {
      return;
    }
    keepSelection.current = true;
    setLinkChoices((prev) => ({ ...prev, [stageId]: linkId }));
  };
  /* eslint-enable react-hooks/set-state-in-effect */

  const templates = preview?.templates || [];
  /** The template text in force for a stage: the recruiter's edit, else stored. */
  const textFor = (t: FirstInviteTemplate) => edits[t.stageId] || { subject: t.subject, body: t.body };

  // Re-rendered on every keystroke: cheap (string substitution over ≤25 rows)
  // and it is the whole point — the table must show the effect of the edit.
  const rows: RenderedRow[] = useMemo(() => {
    const byStage = new Map(templates.map((t) => [t.stageId, textFor(t)]));
    return (preview?.candidates || []).map((c) => {
      const r = renderCandidate(c, byStage.get(c.stageId) || { subject: c.subject || '', body: c.body || '' });
      // "Send Bryq in parallel": each invite mentions the Bryq email (see bryqNote).
      return bryq ? { ...r, body: withBryqNote(r.body, bryqNote, true) } : r;
    });
  }, [preview, edits, bryq, bryqNote]); // eslint-disable-line react-hooks/exhaustive-deps -- textFor reads `edits`

  const chosen = rows.filter((r) => included.has(r.row.opportunityId));
  const flagged = chosen.filter((r) => r.blocked);
  /** In the batch, but unsendable — see canBeSent. Explained rather than left blank. */
  const heldBack = rows.filter((r) => !canBeSent(r.row));

  return (
    <div className="space-y-5">
      <ModalHeader className="!p-0">
        <ModalTitle className="text-lg font-semibold">
          Invite {candidates.length} candidate{candidates.length === 1 ? '' : 's'} — {role.roleTitle}
        </ModalTitle>
      </ModalHeader>

      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Edit the text if you need to, and check what each candidate’s values resolve to. Nothing is
          sent until you confirm.
        </p>
        <LanguageToggle language={language} onChange={setLanguage} />
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Drafting {candidates.length} invitation
          {candidates.length === 1 ? '' : 's'}…
        </div>
      ) : error ? (
        <div
          className="rounded-xl px-4 py-3 text-sm"
          style={{ backgroundColor: COLORS.redBg, color: COLORS.redText }}
        >
          {error}
        </div>
      ) : (
        <>
          {templates.map((template) => (
            <TemplateEditor
              key={template.stageId}
              template={template}
              postingId={role.postingId}
              value={textFor(template)}
              edited={!!edits[template.stageId]}
              onChange={(next) => setEdits((prev) => ({ ...prev, [template.stageId]: next }))}
              onReset={() =>
                setEdits((prev) => {
                  const next = { ...prev };
                  delete next[template.stageId];
                  return next;
                })
              }
              ownEmail={boardEmail}
              onPickLink={(linkId) => pickLink(template.stageId, linkId)}
            />
          ))}

          {heldBack.length > 0 && (
            <div
              className="flex items-start gap-2 rounded-xl px-4 py-3 text-sm"
              style={{ backgroundColor: COLORS.cotton, color: '#484848' }}
              role="status"
            >
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {/* Without this line an unticked box reads as a bug, which is exactly how the
                    old blanket rule was reported. Name them — one row in a table of twenty is
                    not findable by scanning. */}
                {heldBack.length} of {rows.length} could not be pre-selected and cannot be
                emailed: {heldBack.map((r) => r.row.name).join(', ')}. Everyone else is ready to
                go.
              </span>
            </div>
          )}

          <ReviewTable
            rows={rows}
            postingId={role.postingId}
            linkByStage={new Map(templates.map((t) => [t.stageId, t.linkId]))}
            included={included}
            expanded={expanded}
            onToggle={(id) =>
              setIncluded((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            onExpand={(id) => setExpanded((prev) => (prev === id ? null : id))}
          />

          {flagged.length > 0 && (
            <div
              className="flex items-start gap-2 rounded-xl px-4 py-3 text-sm"
              style={{ backgroundColor: COLORS.redBg, color: COLORS.redText }}
              role="status"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {flagged.length} selected candidate{flagged.length === 1 ? ' has' : 's have'} a flagged
                invitation. Fix the text or the interview link, or untick them — an invite with no
                booking link cannot be unsent.
              </span>
            </div>
          )}
        </>
      )}

      <BryqAlongsideField
        value={bryq}
        onChange={setBryq}
        language={language}
        opportunityId={opportunityIds[0]}
        disabled={loading}
      />

      <ModalActions
        confirmLabel={
          chosen.length === 0
            ? 'Select at least one candidate'
            : `Send ${chosen.length} invitation${chosen.length === 1 ? '' : 's'}`
        }
        confirmDisabled={loading || chosen.length === 0}
        // The EDITED text is what gets sent — never the server's original draft.
        onConfirm={() =>
          onConfirm(
            role,
            chosen.map((r) => ({ ...r.row, subject: r.subject, body: r.body, bryq })),
            language
          )
        }
        onCancel={onClose}
      />
    </div>
  );
}

/**
 * Can this invitation leave at all?
 *
 * The ONLY reason to arrive unticked. Deliberately much narrower than "has a
 * blocking warning", which is what it used to be: a missing booking URL or an
 * unresolved token makes an invitation BAD, and the red row, the red banner and
 * the confirm count all say so — but it can still be sent, so the recruiter
 * decides. No email address in Lever, or a candidate who has left the
 * first-interview stages since the board loaded, cannot be sent by anyone; a
 * ticked box there would put a number on the confirm button that the send
 * cannot honour.
 */
function canBeSent(c: FirstInvitePreviewCandidate): boolean {
  if (c.skipped) return false;
  return !(c.warnings || []).includes('no-candidate-email');
}

function LanguageToggle({
  language,
  onChange,
}: {
  language: 'de' | 'en';
  onChange: (l: 'de' | 'en') => void;
}) {
  return (
    <div className="flex shrink-0 gap-1 rounded-full bg-gray-100 p-1">
      {(['de', 'en'] as const).map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => onChange(l)}
          className={`rounded-full px-3 py-1 text-xs font-semibold ${
            language === l ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
          }`}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
